<template>
    <div class="crud-area">
        <div class="container-fluid">
            <div class="crud-header d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 class="crud-title">Informação Institucional</h2>
                    <p class="crud-subtitle">Editar os conteúdos institucionais da fundação</p>
                </div>
                <button class="btn-primary-grad" @click="save" :disabled="saving">
                    <span v-if="saving"><i class="ri-loader-4-line spin"></i> A guardar...</span>
                    <span v-else><i class="ri-save-line"></i> Guardar</span>
                </button>
            </div>

            <div v-if="loading" class="text-center py-5"><div class="spinner-border text-primary"></div></div>
            <div v-if="error" class="alert-error mb-3"><i class="ri-error-warning-line"></i> {{ error }}</div>
            <div v-if="success" class="alert-success mb-3"><i class="ri-checkbox-circle-line"></i> {{ success }}</div>

            <div v-if="!loading" class="crud-card">
                <!-- Missão -->
                <div class="inst-section mb-4">
                    <h4 class="inst-section-title"><i class="ri-focus-3-line"></i> Missão</h4>
                    <div class="row g-3">
                        <div class="col-md-6">
                            <label>Missão (PT)</label>
                            <textarea v-model="form.mission_pt" class="form-input" rows="4"></textarea>
                        </div>
                        <div class="col-md-6">
                            <label>Mission (EN)</label>
                            <textarea v-model="form.mission_en" class="form-input" rows="4"></textarea>
                        </div>
                    </div>
                </div>

                <!-- Visão -->
                <div class="inst-section mb-4">
                    <h4 class="inst-section-title"><i class="ri-eye-line"></i> Visão</h4>
                    <div class="row g-3">
                        <div class="col-md-6">
                            <label>Visão (PT)</label>
                            <textarea v-model="form.vision_pt" class="form-input" rows="4"></textarea>
                        </div>
                        <div class="col-md-6">
                            <label>Vision (EN)</label>
                            <textarea v-model="form.vision_en" class="form-input" rows="4"></textarea>
                        </div>
                    </div>
                </div>

                <!-- Valores -->
                <div class="inst-section mb-4">
                    <h4 class="inst-section-title"><i class="ri-award-line"></i> Valores</h4>
                    <div class="row g-3">
                        <div class="col-md-6">
                            <label>Valores (PT)</label>
                            <textarea v-model="form.values_pt" class="form-input" rows="4"></textarea>
                        </div>
                        <div class="col-md-6">
                            <label>Values (EN)</label>
                            <textarea v-model="form.values_en" class="form-input" rows="4"></textarea>
                        </div>
                    </div>
                </div>

                <!-- História -->
                <div class="inst-section mb-4">
                    <h4 class="inst-section-title"><i class="ri-history-line"></i> História</h4>
                    <div class="row g-3">
                        <div class="col-md-6">
                            <label>História (PT)</label>
                            <textarea v-model="form.history_pt" class="form-input" rows="5"></textarea>
                        </div>
                        <div class="col-md-6">
                            <label>History (EN)</label>
                            <textarea v-model="form.history_en" class="form-input" rows="5"></textarea>
                        </div>
                    </div>
                </div>

                <!-- Estatísticas de Impacto -->
                <div class="inst-section">
                    <h4 class="inst-section-title"><i class="ri-bar-chart-box-line"></i> Estatísticas de Impacto (App Mobile)</h4>
                    <div class="row g-3">
                        <div class="col-md-4">
                            <label>Vidas Impactadas (Beneficiários)</label>
                            <input type="number" v-model.number="stats.beneficiaries" class="form-input" min="0">
                        </div>
                        <div class="col-md-4">
                            <label>Projectos Ativos (Concluídos)</label>
                            <input type="number" v-model.number="stats.projectsCompleted" class="form-input" min="0">
                        </div>
                        <div class="col-md-4">
                            <label>Voluntários</label>
                            <input type="number" v-model.number="stats.volunteers" class="form-input" min="0">
                        </div>
                    </div>
                </div>

                <!-- Brochura Institucional -->
                <div class="inst-section mt-4 mb-4">
                    <h4 class="inst-section-title"><i class="ri-file-pdf-line"></i> Brochura Institucional (PDF)</h4>
                    <div class="row g-3">
                        <div class="col-md-12">
                            <label>Ficheiro PDF da Brochura</label>
                            <div class="input-group">
                                <input type="file" ref="brochureInput" class="form-control" accept="application/pdf" @change="handleBrochureChange">
                                <button v-if="form.brochure_url" class="btn btn-outline-primary" type="button" @click="viewBrochure">
                                    <i class="ri-external-link-line"></i> Ver Atual
                                </button>
                            </div>
                            <small class="text-muted" v-if="form.brochure_url">Documento atual: {{ fileNameFromUrl(form.brochure_url) }}</small>
                            <div v-if="brochureError" class="text-danger mt-1 small">{{ brochureError }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import institutionalService from '../../services/institutionalService';
import impactStatisticsService from '../../services/impactStatisticsService';
import uploadService from '../../services/uploadService';

export default {
    name: 'InstitutionalContent',
    data() {
        return {
            loading: true, saving: false, error: '', success: '',
            form: { mission_pt: '', mission_en: '', vision_pt: '', vision_en: '', values_pt: '', values_en: '', history_pt: '', history_en: '', brochure_url: '' },
            stats: { beneficiaries: 0, projectsCompleted: 0, volunteers: 0 },
            selectedBrochure: null,
            brochureError: '',
        };
    },
    mounted() { this.load(); },
    methods: {
        async load() {
            this.loading = true; this.error = '';
            try {
                const [instData, statsData] = await Promise.all([
                    institutionalService.get(),
                    impactStatisticsService.get()
                ]);

                if (instData) {
                    this.form = {
                        mission_pt: instData.mission_pt || '', mission_en: instData.mission_en || '',
                        vision_pt: instData.vision_pt || '', vision_en: instData.vision_en || '',
                        values_pt: instData.values_pt || '', values_en: instData.values_en || '',
                        history_pt: instData.history_pt || '', history_en: instData.history_en || '',
                        brochure_url: instData.brochure_url || '',
                    };
                }
                
                if (statsData) {
                    this.stats = {
                        beneficiaries: statsData.beneficiaries || 0,
                        projectsCompleted: statsData.projectsCompleted || 0,
                        volunteers: statsData.volunteers || 0,
                    };
                }
            } catch { this.error = 'Erro ao carregar informação.'; }
            finally { this.loading = false; }
        },
        async save() {
            this.saving = true; this.error = ''; this.success = '';
            try {
                if (this.selectedBrochure) {
                    const url = await uploadService.uploadPdf(this.selectedBrochure);
                    this.form.brochure_url = url;
                }

                await Promise.all([
                    institutionalService.update(this.form),
                    impactStatisticsService.update(this.stats)
                ]);
                this.success = 'Informação guardada com sucesso!';
                this.selectedBrochure = null;
                if (this.$refs.brochureInput) this.$refs.brochureInput.value = '';
                setTimeout(() => { this.success = ''; }, 3000);
            } catch { this.error = 'Erro ao guardar. Tente novamente.'; }
            finally { this.saving = false; }
        },
        handleBrochureChange(event) {
            const file = event.target.files[0];
            this.brochureError = '';
            if (file) {
                if (file.type !== 'application/pdf') {
                    this.brochureError = 'Por favor, selecione apenas ficheiros PDF.';
                    this.$refs.brochureInput.value = '';
                    this.selectedBrochure = null;
                    return;
                }
                this.selectedBrochure = file;
            }
        },
        viewBrochure() {
            if (this.form.brochure_url) {
                window.open(this.form.brochure_url, '_blank');
            }
        },
        fileNameFromUrl(url) {
            if (!url) return '';
            const parts = url.split('/');
            return parts[parts.length - 1];
        }
    },
};
</script>

<style lang="scss" scoped>
@import '../Dashboards/GgmfDashboard/ggmf-card';
.inst-section {
    padding-bottom: 24px;
    border-bottom: 1px solid #F0F4FF;
    &:last-child { border-bottom: none; }
}
.inst-section-title {
    font-size: 15px;
    font-weight: 700;
    color: #1765FD;
    margin-bottom: 14px;
    display: flex;
    align-items: center;
    gap: 8px;
    i { font-size: 18px; }
}
</style>
